import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import svgIcons from "../../../assets/svg/svgIcons";
import FTModal from "../../../components/common/FTModal";
import { classifyTrip, TRIP_CLASSIFICATION_LABEL_KEY } from "./tripClassification";
import { tripKeyFor } from "./TripListMultiDay";
import { DateGroup } from "./useMultiDateTripData";

type Props = {
  visible: boolean;
  dateGroups: DateGroup[];
  selectedKeys: string[];
  maxSelectable: number;
  onHide: () => void;
  onConfirm: (keys: string[]) => void;
};

// lets the user pick up to `maxSelectable` trips (across any of the
// selected dates) to view side by side - opened either fresh ("Compare
// Routes") or from the comparison view itself ("Manage Trips Selection"),
// in which case selectedKeys carries the trips currently being compared.
export const CompareRoutesModal: FC<Props> = ({
  visible,
  dateGroups,
  selectedKeys,
  maxSelectable,
  onHide,
  onConfirm,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.tripDetails",
  });
  const [working, setWorking] = useState<string[]>(selectedKeys);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  if (!visible) return null;

  const toggleExpanded = (dateKey: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  const toggleTrip = (tripKey: string) => {
    setWorking((prev) => {
      if (prev.includes(tripKey)) return prev.filter((key) => key !== tripKey);
      if (prev.length >= maxSelectable) return prev;
      return [...prev, tripKey];
    });
  };

  return (
    <FTModal
      visible={visible}
      onHide={onHide}
      position="center"
      containerStyle="max-w-[calc(100vw-4rem)] w-[480px]"
      heading="Compare Routes"
      nodeLeftOfCloseButton={
        <span className="text-xs font-medium text-heading-gray888 mr-3 shrink-0">
          {working.length} of {maxSelectable} Selected
        </span>
      }
    >
      <p className="text-xs text-heading-gray888 -mt-2 mb-3">
        Select up to {maxSelectable} trips to compare side by side
      </p>

      <div className="max-h-[50vh] overflow-auto -mx-2">
        {dateGroups.map((group) => {
          const isExpanded = expandedDates.has(group.dateKey);
          return (
            <div key={group.dateKey} className="border-b border-gray-200">
              <button
                type="button"
                onClick={() => toggleExpanded(group.dateKey)}
                className="w-full flex items-center justify-between px-2 py-2.5 hover:bg-accent-blue-paleO66"
              >
                <span className="flex items-center gap-2 font-semibold text-sm">
                  <span
                    className="inline-block size-3 rounded-full shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label}
                  <span className="text-heading-gray888 font-normal">
                    ({group.trips.length} Trip{group.trips.length === 1 ? "" : "s"})
                  </span>
                </span>
                <img
                  src={svgIcons.LeftArrowIcon}
                  alt=""
                  className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : "-rotate-90"}`}
                />
              </button>

              {isExpanded && (
                <div className="pb-2">
                  {group.trips.map((tripPoints, tripIndex) => {
                    const tripKey = tripKeyFor(group.dateKey, tripIndex);
                    const isChecked = working.includes(tripKey);
                    const isDisabled =
                      !isChecked && working.length >= maxSelectable;
                    const label = t(
                      TRIP_CLASSIFICATION_LABEL_KEY[classifyTrip(tripPoints)]
                    );
                    return (
                      <label
                        key={tripKey}
                        className={`flex items-center gap-2 px-2 py-1.5 text-xs ${isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-accent-blue-paleO66"}`}
                      >
                        <input
                          type="checkbox"
                          className="size-4 accent-accent-blue-dark cursor-pointer disabled:cursor-not-allowed"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => toggleTrip(tripKey)}
                        />
                        <span
                          className="inline-block size-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: group.color }}
                        />
                        Trip {tripIndex + 1} ({label})
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={working.length === 0}
        onClick={() => onConfirm(working)}
        className="w-full mt-4 py-2.5 rounded-lg bg-accent-blue-pale text-accent-blue-dark font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:bg-accent-blue-paleO66"
      >
        Compare ({working.length} trip{working.length === 1 ? "" : "s"})
      </button>
      <button
        type="button"
        onClick={onHide}
        className="w-full mt-2 py-1 text-sm font-medium text-field-error-dark"
      >
        Cancel
      </button>
    </FTModal>
  );
};
